import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { isIntegratorAdmin, useIntegratorMe } from "@/lib/me-query";
import { appsVisibleToUser } from "@/lib/subapp-access";

function accessNeededSearch(appKeys: readonly string[], attemptedPath: string, noModules: boolean): string {
  const params = new URLSearchParams();
  params.set("module", appKeys[0] ?? "hub");
  params.set("target", attemptedPath);
  if (noModules) {
    params.set("nomodules", "1");
  }
  return params.toString();
}

/**
 * Hub module gate: user must have at least one of the listed module keys (integrator admin always passes).
 */
export function SubappAccessGuardAnyOf({
  appKeys,
  children,
}: {
  appKeys: readonly string[];
  children: ReactNode;
}) {
  const location = useLocation();
  const { data: me } = useIntegratorMe();
  if (isIntegratorAdmin(me?.user)) {
    return <>{children}</>;
  }
  const visible = appsVisibleToUser(me);
  const attemptedPath = `${location.pathname}${location.search}`;
  if (visible.length === 0) {
    return (
      <Navigate to={`/access-needed?${accessNeededSearch(appKeys, attemptedPath, true)}`} replace />
    );
  }
  if (!appKeys.some((key) => visible.some((app) => app.key === key))) {
    return (
      <Navigate to={`/access-needed?${accessNeededSearch(appKeys, attemptedPath, false)}`} replace />
    );
  }
  return <>{children}</>;
}
